import { useState } from 'react'
import { Role } from '@/types/user.type'
import { fDateTime } from '@/utils/dayjs'
import { Button } from '@/components/ui/button'
import { useBoolean } from '@/hooks/use-boolean'
import { fCurrency } from '@/utils/format-number'
import { Card, CardContent } from '@/components/ui/card'
import { useAuthContext } from '@/contexts/auth-context/auth-provider'
import { CreditCard, Edit, Package, Truck, User, Eye } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

import OrderDetailModal from './order-detail-modal'
import OrderStatusModal from './order-status-modal'
import { Order, OrderStatus, ShippingStatus } from './types'
import { getShippingStatusBadge, getStatusBadge } from './utils'
import OrderShippingStatusModal from './order-shipping-status-modal'

interface OrdersTableProps {
  orders: Order[]
  loading?: boolean
  onOrderUpdate?: (updatedOrder: Order) => void
}

export default function OrdersTable({
  orders,
  loading = false,
  onOrderUpdate,
}: OrdersTableProps) {
  const { user } = useAuthContext()

  const openChangeShippingStatusModal = useBoolean(false)
  const openChangeOrderStatusModal = useBoolean(false)
  const openOrderDetailModal = useBoolean(false)

  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null)

  const handleOpenChangeShippingStatusModal = (order: Order) => {
    setSelectedOrder(order)
    openChangeShippingStatusModal.onTrue()
  }

  const handleOpenChangeOrderStatusModal = (order: Order) => {
    setSelectedOrder(order)
    openChangeOrderStatusModal.onTrue()
  }

  const handleOpenOrderDetailModal = (order: Order) => {
    setSelectedOrder(order)
    openOrderDetailModal.onTrue()
  }

  const handleOrderUpdate = (updatedOrder: Order) => {
    if (onOrderUpdate) {
      onOrderUpdate(updatedOrder)
    }
  }

  const isAdmin = user?.role === Role.ADMIN
  const isShop = user?.role === Role.SHOP

  if (loading) {
    return (
      <Card className="p-0">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>เลขที่ออเดอร์</TableHead>
                <TableHead>สถานะ</TableHead>
                <TableHead>สถานะการจัดส่ง</TableHead>
                <TableHead>รายการสินค้า</TableHead>
                <TableHead>ผู้สั่งซื้อ</TableHead>
                <TableHead>ยอดรวม</TableHead>
                <TableHead>วันที่อัปเดต</TableHead>
                <TableHead className="text-center">จัดการ</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 10 }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <div className="h-4 bg-gray-200 rounded animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-6 w-20 bg-gray-200 rounded animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-6 w-20 bg-gray-200 rounded animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 bg-gray-200 rounded animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 bg-gray-200 rounded animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 bg-gray-200 rounded animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 bg-gray-200 rounded animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-8 w-8 bg-gray-200 rounded animate-pulse ml-auto" />
                  </TableCell>
                  <TableCell>
                    <div className="h-6 w-6 bg-gray-200 rounded animate-pulse mx-auto" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    )
  }

  if (orders.length === 0) {
    return (
      <div className="border rounded-lg p-8 text-center">
        <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-base text-mute">ไม่พบข้อมูลออเดอร์</p>
      </div>
    )
  }

  const isOrderComplete = (
    status: OrderStatus,
    shippingStatus: ShippingStatus,
  ) =>
    status === OrderStatus.COMPLETED &&
    (shippingStatus === ShippingStatus.DELIVERED ||
      shippingStatus === ShippingStatus.CANCELLED)

  const isOrderShipping = (
    status: OrderStatus,
    shippingStatus: ShippingStatus,
  ) =>
    isAdmin &&
    status === OrderStatus.COMPLETED &&
    shippingStatus !== ShippingStatus.DELIVERED &&
    shippingStatus !== ShippingStatus.CANCELLED

  const canManagePayment = (status: OrderStatus) =>
    isAdmin && status === OrderStatus.PAYMENT
  const canManageShipping = (
    status: OrderStatus,
    shippingStatus: ShippingStatus,
  ) =>
    isShop &&
    status === OrderStatus.COMPLETED &&
    shippingStatus !== ShippingStatus.DELIVERED &&
    shippingStatus !== ShippingStatus.CANCELLED

  return (
    <div>
      <Card className="p-0">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>เลขที่ออเดอร์</TableHead>
                <TableHead>สถานะ</TableHead>
                <TableHead>สถานะการจัดส่ง</TableHead>
                <TableHead>รายการสินค้า</TableHead>
                <TableHead>ผู้สั่งซื้อ</TableHead>
                <TableHead>ยอดรวม</TableHead>
                <TableHead>วันที่อัปเดต</TableHead>
                <TableHead className="text-center">จัดการ</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.map((order) => (
                <TableRow key={order.id}>
                  {/* Order Number */}
                  <TableCell>{order.orderNumber}</TableCell>

                  {/* Status */}
                  <TableCell>{getStatusBadge(order.status)}</TableCell>

                  {/* Shipping Status */}
                  <TableCell>
                    {getShippingStatusBadge(
                      order.shippingStatus || ShippingStatus.PENDING,
                    )}
                  </TableCell>

                  {/* Items */}
                  <TableCell>{order?.items?.length || 0}</TableCell>

                  {/* Customer */}
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gray-400" />
                      <div>
                        {order.customer ? (
                          <p className="text-sm text-black">
                            {order.customer.displayName
                              ? order.customer.displayName
                              : `${order.customer.firstName} ${order.customer.lastName}`}
                          </p>
                        ) : (
                          <p className="text-sm text-muted-foreground">-</p>
                        )}
                        <p className="text-xs text-neutral-500">
                          {order.customer?.phoneNumber || '-'}
                        </p>
                      </div>
                    </div>
                  </TableCell>

                  {/* Total */}
                  <TableCell>{fCurrency(order.finalAmount)}</TableCell>

                  {/* Updated Date */}
                  <TableCell>{fDateTime(order.createdAt)}</TableCell>

                  {/* Actions */}
                  <TableCell className="text-center">
                    {order.status === OrderStatus.PENDING ? (
                      <p>รอดำเนินการ</p>
                    ) : order.status === OrderStatus.CANCEL ? (
                      <p>ยกเลิก</p>
                    ) : order.status === OrderStatus.PAYMENT &&
                      user?.role === Role.SHOP ? (
                      <p>รอแอดมิน</p>
                    ) : isOrderComplete(order.status, order.shippingStatus) ? (
                      <p>สำเร็จ</p>
                    ) : isOrderShipping(order.status, order.shippingStatus) ? (
                      <p>รอจัดส่ง</p>
                    ) : (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm">
                            <Edit className="mr-1 h-3 w-3" />
                            จัดการ
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {canManagePayment(order.status) && (
                            <DropdownMenuItem
                              onClick={() =>
                                handleOpenChangeOrderStatusModal(order)
                              }
                            >
                              <CreditCard className="mr-2 h-4 w-4" />
                              การชำระเงิน
                            </DropdownMenuItem>
                          )}
                          {canManageShipping(
                            order.status,
                            order.shippingStatus,
                          ) && (
                            <DropdownMenuItem
                              onClick={() =>
                                handleOpenChangeShippingStatusModal(order)
                              }
                            >
                              <Truck className="mr-2 h-4 w-4" />
                              สถานะจัดส่ง
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TableCell>

                  {/* View Details */}
                  <TableCell className="text-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenOrderDetailModal(order)}
                      className="h-8 w-8 p-0"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Status Update Modal */}
      <OrderStatusModal
        open={openChangeOrderStatusModal.value}
        onClose={openChangeOrderStatusModal.onFalse}
        order={selectedOrder}
        onOrderUpdate={handleOrderUpdate}
      />

      {/* Shipping Status Update Modal */}
      <OrderShippingStatusModal
        open={openChangeShippingStatusModal.value}
        onClose={openChangeShippingStatusModal.onFalse}
        order={selectedOrder}
        onOrderUpdate={handleOrderUpdate}
      />

      {/* Order Detail Modal */}
      <OrderDetailModal
        open={openOrderDetailModal.value}
        onClose={openOrderDetailModal.onFalse}
        order={selectedOrder}
      />
    </div>
  )
}
